import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Tag, 
  Button, 
  Card, 
  Space, 
  Tabs, 
  Typography, 
  Tooltip, 
  Empty, 
  Spin, 
  Badge, 
  Modal, 
  Form, 
  Input, 
  Select, 
  TimePicker, 
  Switch, 
  Checkbox
} from 'antd';
import { 
  ClockCircleOutlined, 
  CalendarOutlined, 
  EditOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  InfoCircleOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined
} from '@ant-design/icons';
import { FaCalendarAlt, FaTag, FaClock } from 'react-icons/fa';
import moment from 'moment';
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import { 
  getTagAvailability, 
  getAllTagAvailabilities, 
  setTagAvailability, 
  deleteTagAvailability
} from '../../../services/facilityTagAvailabilityService';
import './TagAvailability.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// Define day constants to ensure consistency
const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};

const FacilityTagAvailability = ({ facilityId, facilityTags, refreshData }) => {
  const [tagAvailabilities, setTagAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTabKey, setActiveTabKey] = useState('table');
  const [selectedTag, setSelectedTag] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditingTag, setCurrentEditingTag] = useState(null);
  const [currentDay, setCurrentDay] = useState(null);
  const [form] = Form.useForm();

  // Fetch tag availabilities for the facility
  const fetchTagAvailabilities = async () => {
    try {
      setLoading(true);
      const response = await getAllTagAvailabilities(facilityId);
      if (response?.data?.success) {
        setTagAvailabilities(response.data.data);
        
        // If we have tags but none are selected, select the first one
        if (response.data.data.length > 0 && !selectedTag) {
          setSelectedTag(response.data.data[0].tagName);
        } else if (facilityTags.length > 0 && !selectedTag) {
          // If no tag availabilities but we have facility tags, select the first one
          setSelectedTag(facilityTags[0]);
        }
      } else {
        toast.error("Failed to fetch tag availabilities");
      }
    } catch (error) {
      console.error("Error fetching tag availabilities:", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Initialize selectedTag when facilityTags change
  useEffect(() => {
    if (facilityTags && facilityTags.length > 0 && !selectedTag) {
      setSelectedTag(facilityTags[0]);
    }
  }, [facilityTags]);

  // Initial data fetch
  useEffect(() => {
    if (facilityId) {
      fetchTagAvailabilities();
    }
  }, [facilityId]);

  // Prepare data for the table format
  const getTableData = () => {
    return tagAvailabilities.map((item) => {
      const rowData = {
        key: item.tagName,
        tagName: item.tagName,
        remarks: item.remark || ''
      };

      // Add day data
      DAYS_OF_WEEK.forEach(day => {
        const dayData = item[day];
        
        if (dayData.is24Hours) {
          rowData[day] = '24h';
        } else if (dayData.isReservationOnly) {
          rowData[day] = 'Booking required';
        } else if (dayData.timeRanges && dayData.timeRanges.length > 0) {
          rowData[day] = dayData.timeRanges.map(range => 
            `${range.startTime} – ${range.endTime}`
          ).join(', ');
        } else {
          rowData[day] = '-';
        }
      });

      return rowData;
    });
  };

  // Handle adding a new tag availability
  const handleAddAvailability = () => {
    if (!selectedTag) {
      toast.info("Please select a tag first");
      return;
    }
    
    // Check if the tag already has availability settings
    const existingTag = tagAvailabilities.find(ta => ta.tagName === selectedTag);
    
    if (!existingTag) {
      // Create a new default tag availability object
      const newTagAvailability = {
        facilityId,
        tagName: selectedTag,
        monday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        tuesday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        wednesday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        thursday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        friday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        saturday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        sunday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        remark: ''
      };
      
      // Set the current editing tag and day
      setCurrentEditingTag(selectedTag);
      setCurrentDay('monday');
      
      // Add to the local state
      setTagAvailabilities([...tagAvailabilities, newTagAvailability]);
      
      // Initialize form values
      form.setFieldsValue({
        is24Hours: false,
        isReservationOnly: false,
        dayRemark: '',
        generalRemark: '',
        timeRanges: [{ startTime: null, endTime: null }]
      });
      
      // Show the edit modal
      setShowEditModal(true);
    } else {
      // If the tag already has availability, open the edit modal for Monday
      openDayEditor(selectedTag, 'monday');
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Sn',
      dataIndex: 'key',
      key: 'index',
      width: 60,
      render: (text, record, index) => index + 1
    },
    {
      title: 'Specialist',
      dataIndex: 'tagName',
      key: 'tagName',
      render: (tag) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tag color="blue" style={{ marginRight: 8 }}>
            <FaTag style={{ marginRight: 6 }} />
            {tag}
          </Tag>
        </div>
      )
    },
    ...DAYS_OF_WEEK.map(day => ({
      title: DAY_LABELS[day],
      dataIndex: day,
      key: day,
      render: (text, record) => {
        // Find the tag availability object for this tag
        const tagAvail = tagAvailabilities.find(ta => ta.tagName === record.tagName);
        if (!tagAvail) return <span>-</span>;
        
        const dayData = tagAvail[day];
        
        if (!dayData) return <span>-</span>;
        
        if (dayData.is24Hours) {
          return (
            <Badge 
              status="success" 
              text={<Text type="success" strong>24h</Text>}
            />
          );
        } else if (dayData.isReservationOnly) {
          return (
            <Badge 
              status="processing" 
              text="Booking required"
            />
          );
        } else if (dayData.timeRanges && dayData.timeRanges.length > 0) {
          return (
            <div className="time-ranges">
              {dayData.timeRanges.map((range, idx) => (
                <div key={idx} className="time-range-item">
                  <ClockCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                  {range.startTime} – {range.endTime}
                </div>
              ))}
            </div>
          );
        } else {
          return <span>-</span>;
        }
      }
    })),
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (text, record) => {
        const tagAvail = tagAvailabilities.find(ta => ta.tagName === record.tagName);
        const hasRemark = tagAvail && tagAvail.remark && tagAvail.remark.trim().length > 0;
        
        return hasRemark ? (
          <Tooltip title={tagAvail?.remark}>
            <span className="remarks-text">
              <InfoCircleOutlined style={{ marginRight: 6, color: '#faad14' }} />
              {tagAvail?.remark?.length > 20 
                ? `${tagAvail?.remark?.substring(0, 20)}...` 
                : tagAvail?.remark}
            </span>
          </Tooltip>
        ) : (
          <span>-</span>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (text, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEditClick(record.tagName)}
            style={{ backgroundColor: '#322659', borderColor: '#322659' }}
          >
            Edit
          </Button>
        </Space>
      )
    }
  ];

  // Handle editing a tag's availability
  const handleEditClick = (tagName) => {
    setCurrentEditingTag(tagName);
    setCurrentDay('monday'); // Default to Monday when editing
    
    // Find the tag availability data
    const tagData = tagAvailabilities.find(ta => ta.tagName === tagName);
    
    // If we have data, open the edit modal
    if (tagData) {
      // Set form initial values
      form.setFieldsValue({
        is24Hours: tagData.monday.is24Hours,
        isReservationOnly: tagData.monday.isReservationOnly,
        dayRemark: tagData.monday.remark || '',
        generalRemark: tagData.remark || '',
        timeRanges: tagData.monday.timeRanges.length > 0 
          ? tagData.monday.timeRanges.map(range => ({
              startTime: range.startTime ? moment(range.startTime, 'HH:mm') : null,
              endTime: range.endTime ? moment(range.endTime, 'HH:mm') : null
            }))
          : [{ startTime: null, endTime: null }]
      });
      
      setShowEditModal(true);
    } else {
      // Create a new default tag availability
      const newTagAvailability = {
        facilityId,
        tagName,
        monday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        tuesday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        wednesday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        thursday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        friday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        saturday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        sunday: { is24Hours: false, isReservationOnly: false, remark: '', timeRanges: [] },
        remark: ''
      };
      
      // Initialize form
      form.setFieldsValue({
        is24Hours: false,
        isReservationOnly: false,
        dayRemark: '',
        generalRemark: '',
        timeRanges: [{ startTime: null, endTime: null }]
      });
      
      setTagAvailabilities([...tagAvailabilities, newTagAvailability]);
      setShowEditModal(true);
    }
  };

  // Handle saving tag availability changes
  const handleSaveTagAvailability = async (values) => {
    try {
      if (!currentEditingTag) return;
      
      const tagData = tagAvailabilities.find(ta => ta.tagName === currentEditingTag);
      if (!tagData) return;
      
      const updatedDay = currentDay;
      if (!updatedDay) return;
      
      // Process time ranges to convert moment objects to string format
      let processedTimeRanges = [];
      if (values.timeRanges && !values.is24Hours && !values.isReservationOnly) {
        processedTimeRanges = values.timeRanges
          .filter(range => range.startTime && range.endTime) // Filter out incomplete ranges
          .map(range => ({
            startTime: range.startTime.format('HH:mm'),
            endTime: range.endTime.format('HH:mm')
          }));
      }
      
      // Update the day data
      const updatedDayData = {
        is24Hours: values.is24Hours || false,
        isReservationOnly: values.isReservationOnly || false,
        remark: values.dayRemark || '',
        timeRanges: values.is24Hours || values.isReservationOnly 
          ? [] 
          : processedTimeRanges
      };
      
      // Create a copy of the tag data with the updated day
      const updatedTagData = {
        ...tagData,
        [updatedDay]: updatedDayData,
        remark: values.generalRemark || tagData.remark || ''
      };
      
      // Prepare request data
      const requestData = {
        tagName: currentEditingTag,
        dayAvailability: {
          [updatedDay]: updatedDayData,
          remark: values.generalRemark || tagData.remark || ''
        }
      };
      
      // Send the update to the server
      const response = await setTagAvailability(facilityId, requestData);
      
      if (response?.data?.success) {
        // Update local state
        const updatedAvailabilities = tagAvailabilities.map(ta => 
          ta.tagName === currentEditingTag ? updatedTagData : ta
        );
        
        setTagAvailabilities(updatedAvailabilities);
        toast.success(`Availability for ${currentEditingTag} updated successfully`);
        setShowEditModal(false);
        
        // Call the refresh function if provided
        if (refreshData) {
          refreshData();
        }
      } else {
        toast.error("Failed to update tag availability");
      }
    } catch (error) {
      console.error("Error updating tag availability:", error);
      toast.error(error.message || "An error occurred");
    }
  };

  // Handle opening the day editor modal
  const openDayEditor = (tag, day) => {
    setCurrentEditingTag(tag);
    setCurrentDay(day);
    
    // Find the tag data and day data to populate the form
    const tagData = tagAvailabilities.find(ta => ta.tagName === tag);
    if (!tagData) return;
    
    const dayData = tagData[day];
    if (!dayData) return;
    
    // Set form values
    form.setFieldsValue({
      is24Hours: dayData.is24Hours,
      isReservationOnly: dayData.isReservationOnly,
      dayRemark: dayData.remark || '',
      generalRemark: tagData.remark || '',
      timeRanges: dayData.timeRanges && dayData.timeRanges.length > 0
        ? dayData.timeRanges.map(range => ({
            startTime: range.startTime ? moment(range.startTime, 'HH:mm') : null,
            endTime: range.endTime ? moment(range.endTime, 'HH:mm') : null
          }))
        : [{ startTime: null, endTime: null }]
    });
    
    setShowEditModal(true);
  };

  // Tag Detail View Component
  const TagDetailView = () => {
    if (!selectedTag) {
      return (
        <Empty 
          description="Select a tag to view its availability schedule" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }
    
    // Find the tag data
    const tagData = tagAvailabilities.find(ta => ta.tagName === selectedTag);
    
    // If no tag data exists yet, show empty state with add button
    if (!tagData) {
      return (
        <div className="empty-state">
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p>No availability data found for {selectedTag}</p>
                <p>Click the button below to add availability schedule</p>
              </div>
            }
          />
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAvailability}
              style={{ backgroundColor: '#322659', borderColor: '#322659' }}
            >
              Add Availability for {selectedTag}
            </Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="tag-detail-view">
        <div className="tag-header">
          <Title level={4}>
            <FaTag style={{ marginRight: 8 }} />
            {tagData.tagName} Availability Schedule
          </Title>
          {tagData.remark && (
            <div className="tag-remark">
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 6 }} />
                {tagData.remark}
              </Text>
            </div>
          )}
        </div>
        
        <div className="availability-grid">
          {DAYS_OF_WEEK.map(day => {
            const dayData = tagData[day];
            
            if (!dayData) return null; // Skip if day data doesn't exist
            
            return (
              <Card 
                key={day} 
                title={
                  <div className="day-card-title">
                    <FaCalendarAlt style={{ marginRight: 8 }} />
                    {DAY_LABELS[day]}
                  </div>
                }
                extra={
                  <Button 
                    type="text" 
                    icon={<EditOutlined />} 
                    onClick={() => openDayEditor(tagData.tagName, day)}
                  />
                }
                className={`day-card ${dayData.is24Hours ? 'is-24-hours' : ''} ${dayData.isReservationOnly ? 'is-reservation' : ''}`}
              >
                <div className="day-card-content">
                  {dayData.is24Hours ? (
                    <div className="availability-type-24-hours">
                      <Badge status="success" />
                      <Text strong>Available 24 Hours</Text>
                    </div>
                  ) : dayData.isReservationOnly ? (
                    <div className="availability-type-reservation">
                      <Badge status="processing" />
                      <Text strong>Available by Reservation Only</Text>
                    </div>
                  ) : dayData.timeRanges && dayData.timeRanges.length > 0 ? (
                    <div className="time-ranges-list">
                      {dayData.timeRanges.map((range, idx) => (
                        <div key={idx} className="time-range-card">
                          <FaClock style={{ marginRight: 8, color: '#1890ff' }} />
                          <Text>{range.startTime} – {range.endTime}</Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="availability-type-closed">
                      <Badge status="error" />
                      <Text type="secondary">Closed / Not Available</Text>
                    </div>
                  )}
                  
                  {dayData.remark && (
                    <div className="day-remark">
                      <Text type="secondary">
                        <InfoCircleOutlined style={{ marginRight: 6 }} />
                        {dayData.remark}
                      </Text>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // JSX for the day editor modal
  const renderDayEditorModal = () => {
    // Find the current tag and day data
    const tagData = currentEditingTag 
      ? tagAvailabilities.find(ta => ta.tagName === currentEditingTag) 
      : null;
      
    if (!tagData || !currentDay) return null;
    
    const dayData = tagData[currentDay];
    if (!dayData) return null;
    
    const dayLabel = DAY_LABELS[currentDay];
    
    return (
      <Modal
        title={<span>Edit {tagData.tagName} Availability for {dayLabel}</span>}
        open={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => form.submit()}
            style={{ backgroundColor: '#322659', borderColor: '#322659' }}
          >
            Save Changes
          </Button>
        ]}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveTagAvailability}
          initialValues={{
            is24Hours: dayData.is24Hours,
            isReservationOnly: dayData.isReservationOnly,
            dayRemark: dayData.remark || '',
            generalRemark: tagData.remark || '',
            timeRanges: dayData.timeRanges && dayData.timeRanges.length > 0
              ? dayData.timeRanges.map(range => ({
                  startTime: range.startTime ? moment(range.startTime, 'HH:mm') : null,
                  endTime: range.endTime ? moment(range.endTime, 'HH:mm') : null
                }))
              : [{ startTime: null, endTime: null }]
          }}
        >
          <div className="availability-type-options">
            <Form.Item 
              name="is24Hours" 
              valuePropName="checked"
              style={{ marginBottom: 16 }}
            >
              <Checkbox onChange={(e) => {
                if (e.target.checked) {
                  form.setFieldsValue({ isReservationOnly: false });
                }
              }}>
                <span style={{ fontWeight: 500 }}>Available 24 Hours</span>
              </Checkbox>
            </Form.Item>
            
            <Form.Item 
              name="isReservationOnly" 
              valuePropName="checked"
              style={{ marginBottom: 16 }}
            >
              <Checkbox onChange={(e) => {
                if (e.target.checked) {
                  form.setFieldsValue({ is24Hours: false });
                }
              }}>
                <span style={{ fontWeight: 500 }}>Available by Reservation Only</span>
              </Checkbox>
            </Form.Item>
          </div>
          
          <Form.Item
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues.is24Hours !== currentValues.is24Hours || 
                prevValues.isReservationOnly !== currentValues.isReservationOnly;
            }}
            style={{ marginBottom: 16 }}
          >
            {({ getFieldValue }) => {
              const is24Hours = getFieldValue('is24Hours');
              const isReservationOnly = getFieldValue('isReservationOnly');
              
              return !is24Hours && !isReservationOnly ? (
                <Form.List name="timeRanges">
                  {(fields, { add, remove }) => (
                    <div className="time-ranges-form">
                      <div className="time-ranges-header">
                        <Title level={5}>Time Ranges</Title>
                        <Button 
                          type="dashed" 
                          onClick={() => add()} 
                          icon={<PlusOutlined />}
                        >
                          Add Time Range
                        </Button>
                      </div>
                      
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key} className="time-range-form-item">
                          <Form.Item
                            {...restField}
                            name={[name, 'startTime']}
                            style={{ marginBottom: 0, marginRight: 8 }}
                          >
                            <TimePicker
                              format="HH:mm"
                              placeholder="Start time"
                              className="time-picker"
                            />
                          </Form.Item>
                          <span className="time-separator">to</span>
                          <Form.Item
                            {...restField}
                            name={[name, 'endTime']}
                            style={{ marginBottom: 0, marginRight: 8 }}
                          >
                            <TimePicker
                              format="HH:mm"
                              placeholder="End time"
                              className="time-picker"
                            />
                          </Form.Item>
                          
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              className="remove-time-range-button"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.List>
              ) : null;
            }}
          </Form.Item>
          
          <Form.Item
            name="dayRemark"
            label="Day-specific Remark (optional)"
          >
            <Input.TextArea
              placeholder="Add any specific notes for this day"
              rows={2}
            />
          </Form.Item>
          
          <Form.Item
            name="generalRemark"
            label="General Remark for this Tag (optional)"
          >
            <Input.TextArea
              placeholder="Add general notes for this tag's availability"
              rows={2}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  // Main component render
  return (
    <div className="facility-tag-availability">
      <Card
        title={
          <div className="card-title">
            <FaTag style={{ marginRight: 8 }} />
            <span>Tag Availability Management</span>
          </div>
        }
        className="tag-availability-card"
        extra={
          <Space>
            <Select
              placeholder="Select Tag"
              value={selectedTag || (facilityTags && facilityTags.length > 0 ? facilityTags[0] : null)}
              onChange={setSelectedTag}
              style={{ width: 250 }}
              defaultValue={facilityTags && facilityTags.length > 0 ? facilityTags[0] : null}
              showSearch
              optionFilterProp="children"
              dropdownStyle={{ color: '#000'}}
            >
              {facilityTags && facilityTags.map(tag => (
                <Option key={tag} value={tag}><p style={{color: '#000'}}>{tag}</p></Option>
              ))}
            </Select>
            
            <Button 
              onClick={() => fetchTagAvailabilities()}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Tabs activeKey={activeTabKey} onChange={setActiveTabKey}>
          <TabPane tab="Table View" key="table">
            {loading ? (
              <div className="loading-container">
                <BeatLoader color="#322659" />
                <p className="loading-message">Loading tag availability...</p>
              </div>
            ) : tagAvailabilities.length > 0 ? (
              <Table 
                columns={columns} 
                dataSource={getTableData()} 
                pagination={false}
                className="tag-availability-table"
                rowClassName={(record) => record.key === selectedTag ? 'selected-row' : ''}
                onRow={(record) => ({
                  onClick: () => setSelectedTag(record.key),
                })}
              />
            ) : (
              <div className="empty-state">
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <p>No tag availability data found</p>
                      <p>Go to Detail View to add availability for tags</p>
                    </div>
                  }
                />
              </div>
            )}
          </TabPane>
          
          <TabPane tab="Detail View" key="detail">
            {loading ? (
              <div className="loading-container">
                <BeatLoader color="#322659" />
                <p className="loading-message">Loading tag availability...</p>
              </div>
            ) : (
              <TagDetailView />
            )}
          </TabPane>
        </Tabs>
      </Card>
      
      {renderDayEditorModal()}
    </div>
  );
};

export default FacilityTagAvailability;